<template>
  <div v-if="quote" class="container">
    <div>
      <quote-status-progress />
    </div>
    <div class="configuration-action-buttons__wrapper">
      <div class="column">
        <div v-if="showSaveButton">
          <span>
            <save-quote-button :disabled="false" />
          </span>
        </div>

        <div v-if="showDownloadPdf">
          <download-pdf-button
            :disabled="isPdfButtonDisabled"
            :quote-id="quote.QuoteId"
            :title="pdfButtonText"
            :type="pdfType"
            button-class="primary"
          />
        </div>

        <div :class="showSendOrderButton ? '' : 'hidden'">
          <send-offer
            :visible="showSendOrderButton"
            :disabled="isEmailButtonDisabled"
            :title="emailButtonText"
            :check-customer-type="checkCustomerType"
            :check-expire-date="checkExpireDate"
            :check-expected-delivery-date="checkExpectedDeliveryDate"
          />
        </div>

        <div :class="showSendEmail ? '' : 'hidden'">
          <send-order
            :visible="showSendEmail"
            :disabled="isSendEmailButtonDisabled"
            :check-customer-type="checkCustomerType"
            :check-expire-date="checkExpireDate"
            :check-expected-delivery-date="checkExpectedDeliveryDate"
          />
        </div>

        <banden-label-tool-button />
      </div>
      <div class="column">
        <div :class="showSendOfferButton ? '' : 'hidden'">
          <send-offer
            :visible="showSendOfferButton"
            :disabled="isSendOfferButtonDisabled"
            :title="i18n.t('/QuoteActions/SendOfferButton')"
            :check-customer-type="checkCustomerType"
            :check-expire-date="checkExpireDate"
            :check-expected-delivery-date="checkExpectedDeliveryDate"
          />
        </div>

        <div :class="showSendOrderButton ? '' : 'hidden'">
          <send-order
            :visible="showSendOrderButton"
            :disabled="isSendOrderButtonDisabled"
            :check-customer-type="checkCustomerType"
            :check-expire-date="checkExpireDate"
            :check-expected-delivery-date="checkExpectedDeliveryDate"
          />
        </div>

        <div v-if="showSendToAutolineButton">
          <send-to-autoline />
        </div>

        <div v-if="showEditButton">
          <edit-sales-order
            :show-edit-button="!hasChildQuotes"
            :show-additional-button="showAdditionalOrderButton"
            :quote-id="quote.QuoteId"
            button-class="primary"
          />
        </div>

        <!-- Fallback buttons when approval is enabled but order hasn't been sent for approval -->
        <div v-if="showFallbackButtons" class="fallback-buttons">
          <send-order
            :visible="true"
            :disabled="isSendOrderButtonDisabled"
            :check-customer-type="checkCustomerType"
            :check-expire-date="checkExpireDate"
            :check-expected-delivery-date="checkExpectedDeliveryDate"
          />
        </div>
      </div>
    </div>

    <!-- Approval Actions (only shown in approval mode) -->
    <div v-if="canShowApprovalButtons" class="configuration-action-buttons__approval-actions">
      <app-button
        button-type="primary"
        data-qa="approve-order-button"
        :disabled="isProcessingApproval"
        @click="openApprovalModal('approve')"
      >
        {{ getApprovalButtonTitle() }}
      </app-button>

      <app-button
        button-type="danger"
        data-qa="reject-order-button"
        :disabled="isProcessingApproval"
        @click="openApprovalModal('reject')"
      >
        {{ getRejectButtonTitle() }}
      </app-button>
    </div>

    <div class="configuration-action-warning__wrapper">
      <alert v-if="showNoCustomerAlert" type="info" text="Voeg klant toe om order te versturen." />
    </div>

    <!-- Approval Modal -->
    <approval-modal
      v-if="showApprovalModal"
      :type="approvalType"
      :delivery-date-changed="deliveryDateChanged"
      :new-delivery-date="newDeliveryDate"
      :is-processing="isProcessingApproval"
      @close="closeApprovalModal"
      @confirm="handleApprovalConfirm"
    />
  </div>
</template>

<script>
import SendOffer from '@/components/generic/presentational/organisms/quoteActions/SendOffer.vue'
import SendOrder from '@/components/generic/presentational/organisms/quoteActions/SendOrder.vue'
import SendToAutoline from '@/components/generic/presentational/organisms/quoteActions/SendToAutoline.vue'
import DownloadPdfButton from '@/components/generic/presentational/molecules/downloadButton/DownloadPdfButton.vue'
import SaveQuoteButton from '@/components/generic/presentational/molecules/saveQuoteButton/SaveQuoteButton.vue'
import QuoteStatusProgress from '@/components/generic/presentational/molecules/quoteStatusProgress/QuoteStatusProgress.vue'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import ApprovalModal from '@/components/generic/presentational/molecules/approvalModal/ApprovalModal.vue'
import { mapState, mapGetters } from 'vuex'
import AdditionalOrder from '@/mixins/additionalOrder.js'
import EditSalesOrder from '@/components/generic/presentational/molecules/editSalesOrder/EditSalesOrder.vue'
import managerApprovalService from '@/services/managerApprovalService'
import BandenLabelToolButton from '@/components/generic/presentational/molecules/bandenLabelToolButton/BandenLabelToolButton.vue'

export default {
  components: {
    DownloadPdfButton,
    SendOrder,
    SendOffer,
    SaveQuoteButton,
    Alert,
    QuoteStatusProgress,
    EditSalesOrder,
    SendToAutoline,
    BandenLabelToolButton,
    AppButton,
    ApprovalModal,
  },
  mixins: [AdditionalOrder],
  props: {
    checkCustomerType: {
      type: Boolean,
      default: false,
    },
    checkExpireDate: {
      type: Boolean,
      default: false,
    },
    checkExpectedDeliveryDate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showSendOrderModal: false,
      showApprovalModal: false,
      approvalType: null,
      isProcessingApproval: false,
    }
  },
  computed: {
    // Dynamic namespaces aren't supported by mapGetters
    ...mapState({
      approvalModeEnabled(state, getters) {
        return getters['user/approvalModeEnabled']
      },
      quote(state, getters) {
        return getters[this.quoteNamespace + '/get']
      },
      isLoading(state, getters) {
        return getters[this.quoteNamespace + '/loading']
      },
      quoteCustomer(state, getters) {
        return getters[this.quoteNamespace + '/customer']
      },
      isStatusSalesOrder(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsSalesOrder']
      },
      isStatusQuote(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsQuote']
      },
      isStatusRejected(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsRejected']
      },
      signingStatus(state, getters) {
        return getters[this.quoteNamespace + '/signingStatus']
      },
      isSigningStatusEmpty(state, getters) {
        return getters[this.quoteNamespace + '/quoteSigningStatusIsEmpty']
      },
      isSigningStatusPending(state, getters) {
        return getters[this.quoteNamespace + '/quoteSigningStatusIsPending']
      },
      isSigningStatusCompleted(state, getters) {
        return getters[this.quoteNamespace + '/quoteSigningStatusIsCompleted']
      },
      isStatusConcept(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsConcept']
      },
      isStockOrder(state, getters) {
        return getters[this.quoteNamespace + '/isStockOrder']
      },
      hasOwner(state, getters) {
        return getters[this.quoteNamespace + '/hasOwner']
      },
      hasCustomer(state, getters) {
        return getters[this.quoteNamespace + '/hasCustomer']
      },
      isQuoteNew(state, getters) {
        return getters[this.quoteNamespace + '/isNew']
      },
      customerType(state, getters) {
        return getters[this.quoteNamespace + '/customerType']
      },
      salesOrderSendToAutoline(state, getters) {
        return getters[this.quoteNamespace + '/salesOrderSendToAutoline']
      },
    }),
    ...mapGetters({
      hasChildQuotes: 'quote/hasChildQuotes',
      isConfigurationValid: 'configuration/isConfigurationValid',
      isStockCarAvailable: 'configuration/isStockCarAvailable',
      dealerSettings: 'user/dealerSettings',
    }),
    canShowApprovalButtons() {
      return (
        this.approvalModeEnabled &&
        (this.quote.Status === 'TO_APPROVE' || this.quote.Status === 'APPROVED')
      )
    },
    salesApprovalEnabled() {
      return !!this.dealerSettings?.salesApprovalEnabled
    },
    isStatusSalesOrderWithSigningPending() {
      return (
        this.isStatusSalesOrder && this.isSigningStatusPending && !this.salesOrderSendToAutoline
      )
    },
    isStatusSalesOrderWithSigningCompleted() {
      return (
        this.isStatusSalesOrder && this.isSigningStatusCompleted && !this.salesOrderSendToAutoline
      )
    },
    isStatusSalesOrderInAutoline() {
      return this.isStatusSalesOrder && this.salesOrderSendToAutoline
    },
    showSaveButton() {
      return this.isStatusConcept || this.isStatusQuote || this.isStatusRejected
    },
    showSendOfferButton() {
      return this.isStatusConcept && !this.isStockOrder
    },
    showSendOrderButton() {
      return this.isStatusQuote || (this.isStatusConcept && this.isStockOrder)
    },
    showSendToAutolineButton() {
      if (this.salesApprovalEnabled) {
        return false
      }

      return (
        !this.salesOrderSendToAutoline &&
        (this.isStatusSalesOrderWithSigningPending || this.isStatusSalesOrderWithSigningCompleted)
      )
    },
    showFallbackButtons() {
      // Show fallback buttons if sales approval is enabled and order is in sales order status
      // but hasn't been sent to autoline and hasn't been sent for approval
      return (
        this.salesApprovalEnabled &&
        this.isStatusSalesOrder &&
        !this.salesOrderSendToAutoline &&
        !this.isOrderPendingApproval
      )
    },
    isOrderPendingApproval() {
      return this.quote?.QuoteStatus === 'TO_APPROVE'
    },
    showDownloadPdf() {
      return (
        this.quoteHasValidationMessages ||
        this.isConfigurationInvalid ||
        this.isStatusQuote ||
        this.isStatusSalesOrder
      )
    },
    isConfigurationInvalid() {
      return this.isConfigurationValid === false
    },
    quoteHasValidationMessages() {
      let validationMessages = this.quote?.Metadata?.ValidationMessages
      return validationMessages?.length > 0
    },
    pdfType() {
      if (this.isConceptPDFType) {
        return 'CONCEPT_PDF'
      }
      if (this.isStatusSalesOrder) {
        return 'ORDER_PDF'
      }
      return 'OFFER_PDF'
    },
    pdfButtonText() {
      if (this.isConceptPDFType) {
        return 'Concept PDF'
      }
      if (this.isStatusSalesOrder) {
        return this.i18n.t('/QuoteActions/Download Order Button')
      }
      return this.i18n.t('/QuoteActions/Download Offer Button')
    },
    isConceptPDFType() {
      return this.isConfigurationInvalid || this.quoteHasValidationMessages
    },
    isPdfButtonDisabled() {
      if (this.isLoading) {
        return true
      }

      if (this.isConfigurationInvalid) return false

      if (this.quoteIsInvalid) return true

      return false
    },
    emailButtonText() {
      if (!this.isStatusSalesOrder) return this.i18n.t('/QuoteActions/SendQuoteEmailButton')

      return this.i18n.t('/QuoteActions/SendSalesOrderEmailButton')
    },
    showSendEmail() {
      return (
        this.isStatusSalesOrderWithSigningPending || this.isStatusSalesOrderWithSigningCompleted
      )
    },
    showEditButton() {
      return this.isStatusSalesOrderWithSigningCompleted || this.isStatusSalesOrder
    },
    showAdditionalOrderButton() {
      return this.isStatusSalesOrderInAutoline
    },

    isSendOfferButtonDisabled() {
      if (this.quoteIsInvalid || this.quoteHasValidationMessages || this.isLoading) {
        return true
      }

      if (this.customerCmdmHasFailed || this.isStockOrder) {
        return true
      }

      if ((!this.additionalQuote && this.isQuoteNew) || this.isStatusSalesOrder) {
        // if quote is not saved, or it is a salesorder
        return true
      }

      return false
    },
    isEmailButtonDisabled() {
      if (this.isLoading) {
        return true
      }

      if (this.quoteIsInvalid) return true

      return false
    },
    isSendOrderButtonDisabled() {
      if (this.isStockOrder) {
        return false
      }

      if (
        this.quoteIsInvalid ||
        this.quoteHasValidationMessages ||
        !this.hasCustomer ||
        this.isLoading
      ) {
        // quote should be valid, have a customer and the correct status
        return true
      }
      if (this.isStatusSalesOrder) {
        return true
      }

      if (
        !this.additionalQuote &&
        (this.quoteSendToAutolineHasFailed || this.customerCmdmHasFailed)
      ) {
        return true
      }

      return false
    },

    isSendEmailButtonDisabled() {
      if (this.isStockOrder) {
        return false
      }

      if (this.quoteIsInvalid || !this.hasCustomer || this.isLoading) {
        // quote should be valid, have a customer and the correct status
        return true
      }

      return false
    },

    quoteSendToAutolineHasFailed() {
      // status is quote, but send to autoline failed.
      return this.isStatusQuote && !this.quote.QuoteSendToAutoline
    },
    customerCmdmHasFailed() {
      return this.hasCustomer && !!this.quoteCustomer.CustomerHasCmdmFailed
    },
    quoteIsInvalid() {
      // if configuration invalid, or stockcar is unavailable, or there is no owner
      return (
        (this.isConfigurationInvalid && !this.additionalQuote) ||
        !this.hasOwner ||
        !this.isStockCarAvailable
      )
    },
    showNoCustomerAlert() {
      return !this.hasCustomer && this.isStatusQuote
    },
    deliveryDateChanged() {
      // TODO CST-3979: Implement logic to detect if delivery date was changed during approval
      return false
    },
    newDeliveryDate() {
      // TODO CST-3979: Return formatted new delivery date if changed
      return null
    },
    quoteId() {
      return this.quote?.QuoteId
    },
    quoteStatus() {
      return this.quote?.Status
    },
    currentUser() {
      return window.info?.user?.UserInfo?.UserName
    },
  },
  created() {},
  methods: {
    closeApprovalModal() {
      this.showApprovalModal = false
      this.approvalType = null
    },
    getApprovalButtonTitle() {
      if (this.quoteStatus === 'TO_APPROVE') {
        return this.i18n.t('/ManagerApproval/Actions/Approve')
      }
      if (this.quoteStatus === 'APPROVED') {
        if (this.quote.QuoteDetails.Cars[0].Type === 'CONFIGURATION') {
          return 'Bestellen'
        }
        if (
          this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR' ||
          this.quote.QuoteDetails.Cars[0].Type === 'STOCKCAR_V2'
        ) {
          /*
            TODO:
            for orders with own stockcars “Klant koppelen”
            for orders not owned stockcars “Voertuig overnemen“
           */
          return 'Bestellen'
        }
      }
      throw new Error("Can't determine the correct title for the approval button")
    },
    getRejectButtonTitle() {
      if (this.quoteStatus === 'TO_APPROVE') {
        return this.i18n.t('/ManagerApproval/Actions/Reject')
      }
      return this.i18n.t('/ManagerApproval/Actions/Reject')
    },
    async handleApprovalConfirm(reason) {
      if (!this.quoteId || !this.currentUser) {
        console.error('Missing required data for approval')
        return
      }

      this.isProcessingApproval = true

      try {
        if (this.approvalType === 'approve') {
          await managerApprovalService.approveOrder(this.quoteId, this.quoteStatus, reason)
        } else if (this.approvalType === 'reject') {
          await managerApprovalService.rejectOrder(this.quoteId, this.quoteStatus, reason)
        }

        // Close modal and navigate back
        this.closeApprovalModal()

        // Navigate back to the approval dashboard
        const returnUrl = this.$route.query.returnUrl
        const tabParam = this.$route.query.tab

        if (returnUrl) {
          // If we have a returnUrl, use it and preserve the tab parameter
          if (tabParam !== undefined) {
            // Add tab parameter to the return URL
            const separator = returnUrl.includes('?') ? '&' : '?'
            this.$router.push(`${returnUrl}${separator}tab=${tabParam}`)
          } else {
            this.$router.push(returnUrl)
          }
        } else {
          // Default navigation, preserve tab parameter if present
          const query = {}
          if (tabParam !== undefined) {
            query.tab = tabParam
          }

          this.$router.push({
            name: 'quote-overview-page',
            params: {
              cstType: this.$route.params.cstType,
              establishmentId: this.$route.params.establishmentId,
            },
            query,
          })
        }
      } catch (error) {
        console.error('Error processing approval:', error)
        alert('Er is een fout opgetreden bij het verwerken van de beoordeling.')
      } finally {
        this.isProcessingApproval = false
      }
    },
    openApprovalModal(type) {
      this.approvalType = type
      this.showApprovalModal = true
    },
    toggleSendOrderModal(show) {
      this.showSendOrderModal = show
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.quoteActionsButton),
.quoteActionsButtonWrapper {
  width: 100%;
}

.configuration-action-buttons__wrapper {
  display: flex;
  justify-content: space-between;
  flex-direction: row;

  div {
    padding-bottom: 0.5em;

    &.hidden {
      padding: 0;
      height: 0;
    }
  }

  column {
    flex: 1;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }
}

.configuration-action-buttons__order-actions label {
  margin-right: 2em;
  font-weight: bold;
}

.configuration-action-buttons__quote-actions {
  display: inline-block;
}

.configuration-action-warning__wrapper {
  display: flex;
  justify-content: flex-end;
}

.container {
  display: flex;
  flex-direction: column;
}

.fallback-buttons {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.configuration-action-buttons__approval-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
}
</style>
